import { Component, Input, OnInit, ChangeDetectorRef, AfterViewInit, OnDestroy, HostListener } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { Toast, ToastrService, ToastPackage } from 'ngx-toastr';
import { AlertType, SecurityAlert, SecurityAlert as SecurityContext } from './models/security-alert';
import { ImageService } from '@app/shared/services/image.service';

@Component({
  selector: 'app-security-alert',
  templateUrl: './security-alert.component.html',
  styleUrls: ['./security-alert.component.scss']
})
export class SecurityAlertComponent extends Toast implements AfterViewInit, OnDestroy {

  safeHtml: SafeHtml;
  AlertType = AlertType;

  @Input() progress: number;
  timeoutProgress: number = 100;

  // Track original timeout and remaining time for pause/resume
  private originalTimeOut: number = 0;
  private timeRemaining: number = 0;

  private _alert: SecurityAlert;
  private timeoutInterval: any;

  // Cache image URL to prevent ExpressionChangedAfterItHasBeenCheckedError
  public cachedImageUrl: string;


  @Input()
  set alert(newAlert: SecurityAlert) {
    this._alert = { ...newAlert }; // Create new object reference
    this.safeHtml = this.sanitizer.bypassSecurityTrustHtml(newAlert.content); // Sanitize the content when set

    // Cache image URL to prevent ExpressionChangedAfterItHasBeenCheckedError
    if (newAlert.photoUrl && this.imageService) {
      this.cachedImageUrl = this.imageService.getAdaptiveImage(newAlert.photoUrl);
    }

    // Manual change detection trigger - use setTimeout to avoid constructor issues
    if (this.cdr) {
      setTimeout(() => {
        this.cdr.detectChanges();
      }, 0);
    }
  }

  get alert(): SecurityAlert {
    return this._alert;
  }

  get alertCount(): number {
    return this._alert?.count || 1;
  }

  get shouldShowCount(): boolean {
    return this.alertCount > 1;
  }

  constructor(protected toastrService: ToastrService, public toastPackage: ToastPackage, protected sanitizer: DomSanitizer,
    public imageService: ImageService, private cdr: ChangeDetectorRef) {
    super(toastrService, toastPackage);

    const parsedAlert = JSON.parse(this.message);
    this.alert = parsedAlert;

    // Initialize cached image URL if needed
    if (parsedAlert.photoUrl) {
      this.cachedImageUrl = this.imageService.getAdaptiveImage(parsedAlert.photoUrl);
    }
  }

  ngAfterViewInit() {
    // Start timeout progress animation if timeout is enabled
    // Sync with toastr's actual timeout start
    this.originalTimeOut = this.toastPackage.config.timeOut || 0;
    this.timeRemaining = this.originalTimeOut;
    this.startTimeoutProgress();
  }

  /**
   * Start or resume the timeout progress animation.
   * @param remaining (optional) Remaining time in ms. If not provided, uses timeRemaining.
   */
  private startTimeoutProgress(remaining?: number) {
    // Only show progress if timeout is enabled and positive
    if (this.toastPackage.config.disableTimeOut || this.originalTimeOut <= 0) {
      return;
    }

    this.timeRemaining = remaining ?? this.timeRemaining;

    const updateInterval = 50; // 50 ms for smooth update

    // Clear any existing interval before starting new one
    if (this.timeoutInterval) {
      clearInterval(this.timeoutInterval);
    }

    this.timeoutInterval = setInterval(() => {
      this.timeRemaining -= updateInterval;

      // Prevent negative values
      this.timeRemaining = Math.max(0, this.timeRemaining);

      this.timeoutProgress = (this.timeRemaining / this.originalTimeOut) * 100;

      // When finished clear interval
      if (this.timeRemaining <= 0) {
        clearInterval(this.timeoutInterval);
        this.timeoutInterval = null;
        // Auto dismiss toast when countdown completes
        this.toastrService.clear(this.toastPackage.toastId);
        return;
      }

      this.cdr.detectChanges();
    }, updateInterval);
  }

  /**
   * Pause the timeout progress (on mouse enter)
   */
  @HostListener('mouseenter')
  onMouseEnter() {
    if (this.timeoutInterval) {
      clearInterval(this.timeoutInterval);
    }
  }

  /**
   * Resume the timeout progress (on mouse leave)
   */
  @HostListener('mouseleave')
  onMouseLeave() {
    // Do not resume if already completed or disabled
    if (this.timeRemaining > 0 && !this.toastPackage.config.disableTimeOut) {
      this.startTimeoutProgress(this.timeRemaining);
    }
  }

  dismiss() {
    if (this.timeoutInterval) {
      clearInterval(this.timeoutInterval);
    }
    this.toastrService.clear(this.toastPackage.toastId);
  }

  ngOnDestroy() {
    if (this.timeoutInterval) {
      clearInterval(this.timeoutInterval);
    }
  }

}
